import React, { FC, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Button, Col, Row } from "reactstrap";
import Swal from "sweetalert2";
import { ReactTyped } from "react-typed";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { manpowerApis } from "../../../api/ManpowerNorms";
import adminOrgApis from "../../../api/master/adminOrg";
import { ApiResponse } from "../../../api/master/axiosIndex";

// Add/edit ONE flat manpower-norm row (Item Code, Item Description, Units/UOM,
// PUC Factor, Unit Price). Unlike the material norm form there are NO material
// lines — a manpower type is a single editable rate-card row.

type ManpowerNormForm = {
  itemCode: string;
  itemDescription?: string;
  uomId?: number;
  pucFactor?: number;
  unitPrice?: number;
};

const defaultForm: ManpowerNormForm = {
  itemCode: "",
  itemDescription: "",
  uomId: undefined,
  pucFactor: 1,
  unitPrice: undefined,
};

const ManpowerNormModal: FC<{
  open: boolean;
  /** manpowerNormId when editing, null/undefined when creating. */
  id?: number | null;
  /** Scope for a NEW row — sent on create only (immutable afterwards). */
  category?: "PRW" | "OTHERS";
  projectId?: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, id, category = "PRW", projectId = 0, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uomOptions, setUomOptions] = useState<SelectOption[]>([]);
  const [warning, setWarning] = useState<{
    error?: string;
    message?: string;
  }>({});

  const methods = useForm<ManpowerNormForm>({
    mode: "onChange",
    defaultValues: { ...defaultForm },
  });
  const { handleSubmit, reset, setValue } = methods;

  const isEdit = !!id;
  const resetWarning = () => setWarning({});

  const fetchUomOptions = async () => {
    try {
      const response = await adminOrgApis.fetchUom();
      const { data, status } = response || {};
      if (status && Array.isArray(data)) {
        setUomOptions(
          data.map((item: any) => ({
            value: item?.workUomId ?? item?.uomId ?? item?.id,
            label: item?.uomShortCode
              ? `${item?.uomName} (${item?.uomShortCode})`
              : item?.uomName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching UOMs:", error);
    }
  };

  const fetchById = async (manpowerNormId: number) => {
    try {
      setLoading(true);
      const apiResponse: ApiResponse = await manpowerApis.getById(manpowerNormId);
      const { data, status } = apiResponse;
      if (status && data) {
        setValue("itemCode", data?.itemCode ?? "");
        setValue("itemDescription", data?.itemDescription ?? "");
        setValue(
          "uomId",
          data?.uomId !== undefined && data?.uomId !== null
            ? Number(data.uomId)
            : undefined
        );
        setValue(
          "pucFactor",
          data?.pucFactor !== undefined && data?.pucFactor !== null
            ? Number(data.pucFactor)
            : 1
        );
        setValue(
          "unitPrice",
          data?.unitPrice !== undefined && data?.unitPrice !== null
            ? Number(data.unitPrice)
            : undefined
        );
      }
    } catch (error) {
      console.error("Error loading manpower norm:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    resetWarning();
    reset({ ...defaultForm });
    fetchUomOptions();
    if (id) {
      fetchById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const buildPayload = (form: ManpowerNormForm) => {
    const payload: any = {
      itemCode: form.itemCode?.trim(),
      itemDescription: form.itemDescription?.trim() || undefined,
      uomId:
        form.uomId !== undefined && form.uomId !== null
          ? Number(form.uomId)
          : undefined,
      pucFactor:
        form.pucFactor !== undefined &&
        form.pucFactor !== null &&
        String(form.pucFactor) !== ""
          ? Number(form.pucFactor)
          : undefined,
      unitPrice:
        form.unitPrice !== undefined &&
        form.unitPrice !== null &&
        String(form.unitPrice) !== ""
          ? Number(form.unitPrice)
          : undefined,
    };
    return payload;
  };

  const onSubmit = async (form: ManpowerNormForm) => {
    try {
      resetWarning();
      setSubmitting(true);
      const payload = buildPayload(form);
      const response: ApiResponse = isEdit
        ? await manpowerApis.update({ manpowerNormId: id as number, ...payload })
        : await manpowerApis.create({ ...payload, category, projectId });
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Updated Successfully" : "Created Successfully",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        onSuccess();
        onClose();
      } else {
        setWarning({ message, error });
      }
    } catch (error) {
      console.error("Error saving manpower norm:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={isEdit ? "Edit Manpower Norm" : "Add Manpower Norm"}
      open={open}
      footer={null}
      width={720}
      onCancel={onClose}
      destroyOnClose
    >
      {loading ? (
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      ) : (
        <FormProvider {...methods}>
          <form>
            <Alert
              isOpen={warning?.message !== undefined}
              color="danger"
              className="start-0 w-100 py-1 mt-1 mb-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <span className="fw-bold">{warning?.error}: </span>
              <ReactTyped
                strings={[warning?.message || ""]}
                typeSpeed={10}
                showCursor={false}
              />
            </Alert>
            <div className="p-1">
              <Row className="w-100 mt-1">
                <Col lg="6">
                  <FormInput
                    name="itemCode"
                    label="Item Code"
                    placeholder="e.g. MP-MASON"
                    type="text"
                    required
                    maxLength={100}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="itemDescription"
                    label="Item Description"
                    placeholder="e.g. Mason - Skilled"
                    type="text"
                    maxLength={255}
                  />
                </Col>
              </Row>
              <Row className="w-100 mt-2">
                <Col lg="4">
                  <FormSelect
                    name="uomId"
                    label="Units"
                    placeholder="Select unit"
                    options={uomOptions}
                  />
                </Col>
                <Col lg="4">
                  <FormInput
                    name="pucFactor"
                    label="PUC Factor"
                    type="number"
                    pattern={/^\d+(\.\d{1,4})?$/}
                    min={0}
                    placeholder="Purchase unit conversion, default 1"
                    customValidation={{
                      validate: (value: any) =>
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        /^\d+(\.\d{1,4})?$/.test(String(value)) ||
                        "PUC factor allows max 4 decimals",
                    }}
                  />
                </Col>
                <Col lg="4">
                  <FormInput
                    name="unitPrice"
                    label="Unit Price (₹)"
                    type="number"
                    pattern={/^\d+(\.\d{1,4})?$/}
                    min={0}
                    placeholder="Rate per unit"
                    customValidation={{
                      validate: (value: any) =>
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        /^\d+(\.\d{1,4})?$/.test(String(value)) ||
                        "Unit price allows max 4 decimals",
                    }}
                  />
                </Col>
              </Row>
            </div>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      disabled={submitting}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {isEdit ? "Update" : "Save"}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onClose();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      )}
    </Modal>
  );
};

export default ManpowerNormModal;
